/**
 * @fileoverview
 * Region Service - Manages region master data
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, of, map } from 'rxjs';
import { ApiService } from '@core/http';
import { API_ENDPOINTS } from '@core/constants';

export interface Region {
  id: number;
  uuid: string;
  name: string;
  code: string;
  isActive: boolean;
}

export interface RegionDropdownOption {
  value: string; // uuid
  label: string; // name
  id: number;
}

@Injectable({
  providedIn: 'root'
})
export class RegionService {
  private readonly apiService = inject(ApiService);

  // State signals for dropdown
  private readonly regionsSignal = signal<RegionDropdownOption[]>([]);
  private readonly loadingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<string | null>(null);

  // Public readonly signals
  readonly regions = this.regionsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  /**
   * Get regions for dropdown.
   * @param options.all - When true, request all regions regardless of user access (backend query param).
   */
  getRegionsForDropdown(options?: { all?: boolean }): Observable<{ data: RegionDropdownOption[] }> {
    const useAll = options?.all === true;
    // Return cached data only when not requesting all (cache is for default access-scoped list)
    if (!useAll && this.regionsSignal().length > 0) {
      return of({ data: this.regionsSignal() });
    }

    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    const requestOptions = useAll ? { params: { all: 'true' as const } } : undefined;
    return this.apiService.get<{ data: Region[] }>(API_ENDPOINTS.REGIONS.DROPDOWN, requestOptions).pipe(
      map(response => {
        // Transform backend data to dropdown format
        const dropdownOptions = (response.data || []).map(region => ({
          value: region.uuid,
          label: region.name,
          id: region.id,
        }));
        this.regionsSignal.set(dropdownOptions);
        this.loadingSignal.set(false);
        return { data: dropdownOptions };
      }),
      catchError(error => {
        this.errorSignal.set(error.message || 'Failed to load regions');
        this.loadingSignal.set(false);
        this.regionsSignal.set([]);
        return of({ data: [] });
      })
    );
  }

  /**
   * Get region by UUID
   */
  getRegionByUuid(uuid: string): Observable<Region> {
    return this.apiService.get<Region>(API_ENDPOINTS.REGIONS.BY_UUID(uuid));
  }

  /**
   * Clear cached regions (force reload)
   */
  clearCache(): void {
    this.regionsSignal.set([]);
    this.errorSignal.set(null);
  }
}
